export const posClientPricingContext = (meta = {}) => Object.fromEntries(Object.entries(meta).filter(([key]) => key.startsWith('clientPricing')));

export const posSnapshotKey = (varietyId, sourcePriceId, cartLineId) => cartLineId ? `scale:${cartLineId}` : `${varietyId}:${sourcePriceId}`;

export const resolvePosPriceSnapshot = (snapshots, { variety, sourcePrice, selectedPrice, cartLineId, selection = '', clientId, clientPricingContext = {}, now = Date.now() }) => {
  const key = posSnapshotKey(variety.id, sourcePrice.id, cartLineId);
  let snapshot = snapshots.get(key);
  if (!snapshot || snapshot.selection !== selection) {
    snapshot = { selection, priceId: selectedPrice.id, price: Number(selectedPrice.price), priceRow: selectedPrice, now };
    snapshots.set(key, snapshot);
  }
  const clientKey = String(clientId ?? '');
  if (!Object.prototype.hasOwnProperty.call(snapshot, 'clientPricing') || snapshot.clientId !== clientKey) {
    snapshot.clientId = clientKey;
    snapshot.clientPricing = clientPricingContext;
  }
  const live = (variety.VarietyPrices || []).find((price) => String(price.id) === String(snapshot.priceId));
  return { ...(live || snapshot.priceRow), price: snapshot.price,
    regularPrice: snapshot.priceRow.regularPrice ?? snapshot.price,
    effectivePrice: snapshot.price,
    priceSnapshot: snapshot.priceRow.priceSnapshot,
    posClientPricing: snapshot.clientPricing,
    posPriceContext: { promotions: snapshot.priceRow.promotions || [], now: snapshot.priceRow.snapshotNow || snapshot.now || Date.now() },
    ...(live ? {} : { isSoldOut: true, catalogRemoved: true }) };
};

export const prunePosPriceSnapshots = (snapshots, products, quantities, scaleLines) => {
  const active = new Set((scaleLines || []).map((line) => posSnapshotKey(line.varietyId, line.varietyPriceId, line.cartLineId)));
  (products || []).forEach((product) => (product.Varieties || []).forEach((variety) => (variety.VarietyPrices || []).forEach((price, index) => {
    if (Number(quantities?.[variety.id]?.[index]) > 0) active.add(posSnapshotKey(variety.id, price.id));
  })));
  snapshots.forEach((_value, key) => { if (!active.has(key)) snapshots.delete(key); });
};

export const retainUnavailableCartRows = (nextProducts, previousProducts, quantities, scaleLines = []) => {
  const active = new Set(scaleLines.map((line) => `${line.varietyId}:${line.varietyPriceId}`));
  const result = [...nextProducts];
  previousProducts.forEach((oldProduct) => {
    (oldProduct.Varieties || []).forEach((oldVariety) => {
      (oldVariety.VarietyPrices || []).forEach((oldPrice, index) => {
        if (!(Number(quantities?.[oldVariety.id]?.[index]) > 0) && !active.has(`${oldVariety.id}:${oldPrice.id}`)) return;
        let productIndex = result.findIndex((product) => String(product.id) === String(oldProduct.id));
        if (productIndex < 0) { productIndex = result.length; result.push({ ...oldProduct, Varieties: [], catalogRemoved: true }); }
        const product = result[productIndex];
        const varieties = [...(product.Varieties || [])];
        let varietyIndex = varieties.findIndex((variety) => String(variety.id) === String(oldVariety.id));
        if (varietyIndex < 0) { varietyIndex = varieties.length; varieties.push({ ...oldVariety, VarietyPrices: [], isSoldOut: true, catalogRemoved: true }); }
        const variety = varieties[varietyIndex];
        if ((variety.VarietyPrices || []).some((price) => String(price.id) === String(oldPrice.id))) return;
        varieties[varietyIndex] = { ...variety, VarietyPrices: [...(variety.VarietyPrices || []), { ...oldPrice, isSoldOut: true, catalogRemoved: true }] };
        result[productIndex] = { ...product, Varieties: varieties };
      });
    });
  });
  return result;
};

export const preserveActiveLineDiscounts = ({ previousProducts, products, quantities, previousDiscounts, discounts }) => {
  const result = { ...discounts };
  const previous = new Map();
  previousProducts.forEach((product) => (product.Varieties || []).forEach((variety) => (variety.VarietyPrices || []).forEach((price, index) => {
    if (Number(quantities?.[variety.id]?.[index]) > 0) previous.set(`${variety.id}:${price.id}`, previousDiscounts?.[variety.id]?.[index] || 0);
  })));
  products.forEach((product) => (product.Varieties || []).forEach((variety) => (variety.VarietyPrices || []).forEach((price, index) => {
    const key = `${variety.id}:${price.id}`;
    if (previous.has(key)) { result[variety.id] = [...(result[variety.id] || [])]; result[variety.id][index] = previous.get(key); }
  })));
  return result;
};

// The server validates the signature on save. Decoding here only restores the
// same display context that the server previously issued for this sale line.
export const restorePosPriceRow = (item, livePrice) => {
  let context;
  try { context = JSON.parse(atob(item.priceSnapshot.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); } catch { context = null; }
  return { ...livePrice, price: Number(item.price), regularPrice: context?.regularPrice ?? Number(item.price),
    priceSnapshot: item.priceSnapshot, posClientPricing: posClientPricingContext(item), promotions: context?.promotions || [], snapshotNow: context?.now };
};
